import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, Home, LifeBuoy, LayoutDashboard } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import BondAnimatedLogo from './BondAnimatedLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar({ onRegisterClick }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [loginUrl, setLoginUrl] = useState('https://osintlab.it/it/auth/login');
  const [loginPath, setLoginPath] = useState('/portal/login');
  
  // Link al sito Smart Info
  const smartInfoUrl = 'https://www.smart-info.it/bond';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        if (response.data.osintlab_login_url) {
          setLoginUrl(response.data.osintlab_login_url);
        }
        setLoginPath(response.data.login_mode === 'eddi' ? '/login' : '/portal/login');
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b-[2px] border-[#D4AF37]/70" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-[80px]">
          
          {/* Logo - animated BOND + "by Smart Info" badge adjacent */}
          <div className="flex items-center gap-3" data-testid="logo-group">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <BondAnimatedLogo variant="v3" style={{ width: '220px', height: '56px' }} />
            </Link>
            <a
              href="https://www.smart-info.it"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 pl-3 ml-1 border-l border-[#D4AF37]/25 hover:opacity-100 opacity-90 transition-opacity"
              data-testid="navbar-smartinfo-badge"
            >
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">by</span>
              <img
                src="/assets/smart_info_logo.png"
                alt="Smart Info"
                className="h-7 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation - Right side */}
          <div className="hidden md:flex items-center gap-3">
            
            {/* HOME - Link a Smart Info */}
            <a 
              href={smartInfoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:text-white transition-all text-[14px] font-normal hover:bg-white/5 rounded"
              data-testid="nav-home-link"
            >
              <Home size={16} />
              <span>HOME</span>
            </a>

            {/* CONTATTI E ASSISTENZA - Link a smart-info.it/contatti */}
            <a
              href="https://www.smart-info.it/contatti"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:text-white transition-all text-[14px] font-normal hover:bg-white/5 rounded"
              data-testid="nav-contacts-link"
            >
              <LifeBuoy size={16} />
              <span>CONTATTI</span>
            </a>

            {/* Divider */}
            <div className="h-6 w-px bg-white/10 mx-2"></div>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-2.5 text-gray-400 hover:text-white transition-all hover:bg-white/5 rounded"
              data-testid="language-toggle"
            >
              <Globe size={15} />
              <span className="text-[13px] font-normal uppercase">{language}</span>
            </button>

            {/* ACCEDI / DASHBOARD - cambia se utente è loggato */}
            {isAuthenticated ? (
              <button 
                onClick={() => navigate('/dashboard')}
                className="bond-btn bond-btn-secondary bond-btn-small"
                data-testid="nav-dashboard-link"
              >
                <LayoutDashboard size={14} />
                Dashboard
              </button>
            ) : (
              <button 
                onClick={() => navigate(loginPath)}
                className="bond-btn bond-btn-secondary bond-btn-small"
                data-testid="nav-login-link"
              >
                Accedi
              </button>
            )}

            {/* ABBONATI / ESCI */}
            {isAuthenticated ? (
              <button 
                onClick={() => { logout(); navigate('/'); }}
                className="bond-btn bond-btn-primary bond-btn-small"
                data-testid="nav-logout-btn"
              >
                Esci
              </button>
            ) : (
              <button 
                onClick={() => navigate('/subscribe')}
                className="bond-btn bond-btn-primary bond-btn-small"
                data-testid="nav-register-btn"
              >
                Abbonati
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#050505] border-t border-[#D4AF37]/20">
          <div className="px-6 py-5 space-y-3">
            
            {/* HOME */}
            <a 
              href={smartInfoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-3 text-gray-300 text-[15px] border-b border-white/5"
              data-testid="mobile-home-link"
            >
              <Home size={18} />
              <span>HOME - Smart Info</span>
            </a>

            {/* CONTATTI E ASSISTENZA */}
            <a
              href="https://www.smart-info.it/contatti"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-3 text-gray-300 text-[15px] border-b border-white/5"
              data-testid="mobile-contacts-link"
            >
              <LifeBuoy size={18} />
              <span>Contatti e Assistenza</span>
            </a>
            
            {/* Language */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-3 py-3 text-gray-400 w-full"
              data-testid="mobile-language-toggle"
            >
              <Globe size={18} />
              <span className="text-[14px]">{language === 'it' ? 'English' : 'Italiano'}</span>
            </button>

            <div className="pt-3 space-y-3">
              {/* ACCEDI */}
              <button 
                onClick={() => { setMobileMenuOpen(false); navigate(loginPath); }}
                className="bond-btn bond-btn-secondary w-full"
                data-testid="mobile-login-link"
              >
                Accedi
              </button>
              
              {/* ABBONATI */}
              <button 
                onClick={() => { setMobileMenuOpen(false); navigate('/subscribe'); }}
                className="bond-btn bond-btn-primary w-full"
                data-testid="mobile-register-btn"
              >
                Abbonati
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
